<template>
    <div class="p-4" style="width: 1020px">
        <div class="white-background">
            <v-row no-gutters>
                <v-col cols="4" class="pt-4 px-2">
                    <div class="ml-2 mt-2">
                        <RadioButton
                            v-for="option in radioOptions"
                            :key="option.value"
                            :label="option.label"
                            v-model="radioState"
                            :buttonValue="option.value"
                            class="py-1"
                        />
                        <RadioButton
                            :label="'From date to today' | t"
                            v-model="radioState"
                            :buttonValue="'customDateRange'"
                            class="py-1"
                            @input="onRepeatedClick('customDateRange')"
                        />
                    </div>
                </v-col>
                <v-divider class="my-4" vertical></v-divider>
                <v-col cols="7" class="py-2 px-4">
                    <div>
                        <date-picker
                            ref="datePickerRef"
                            v-model="selectedDates"
                            :show-day-popover="false"
                            :is-inline="true"
                            :first-day-of-week="2"
                            :columns="2"
                            mode="range"
                            class="custom-calendar"
                            color="red"
                        >
                            <template v-slot:header-left-button="{ nav }">
                                <div class="nav-button">
                                    <v-icon class="mx-2 my-2 large-icon">chevron_left</v-icon>
                                </div>
                            </template>

                            <template v-slot:header-right-button="{ nav }">
                                <div class="nav-button">
                                    <v-icon class="mx-2 my-2 large-icon">chevron_right</v-icon>
                                </div>
                            </template>
                        </date-picker>
                    </div>
                </v-col>
            </v-row>
        </div>

        <v-dialog v-model="dateDialog" persistent max-width="400px">
            <v-card>
                <v-card-title>{{ "From date" | t }}</v-card-title>
                <v-card-text class="d-flex justify-center align-center pb-0">
                    <date-picker
                        v-model="customDateFrom"
                        :show-day-popover="false"
                        :is-inline="true"
                        :first-day-of-week="2"
                        :columns="1"
                        class="custom-calendar"
                        color="red"
                    >
                        <template v-slot:header-left-button="{ nav }">
                            <div class="nav-button">
                                <v-icon class="mx-2 my-2 large-icon">chevron_left</v-icon>
                            </div>
                        </template>

                        <template v-slot:header-right-button="{ nav }">
                            <div class="nav-button">
                                <v-icon class="mx-2 my-2 large-icon">chevron_right</v-icon>
                            </div>
                        </template>
                    </date-picker>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click.stop="resolveDateDialog(false)">{{ "Cancel" | t }}</v-btn>
                    <v-btn color="primary" text @click.stop="resolveDateDialog(true)">{{ "Confirm" | t }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import RadioButton from "@/components/input/radio-button.vue";
import TriggerField from "@/components/dropdown/trigger/trigger-field.vue";

export default {
    components: {
        TriggerField,
        DatePicker,
        RadioButton,
    },
    props: {
        formatEmit: {
            type: Boolean,
            default: true,
        },
        initDates: {
            type: Object,
            default: null,
        },
        initRadioState: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            radioState: null,
            dateDialog: false,
            menu: false,
            customDateFrom: null,
            radioOptions: [
                {
                    label: "Current month",
                    value: "currentMonth",
                },
                {
                    label: "Last 3 months",
                    value: "last3Months",
                },
                {
                    label: "Last 6 months",
                    value: "last6Months",
                },
                {
                    label: "Last 12 months",
                    value: "last12Months",
                },
                {
                    label: "Last 24 months",
                    value: "last24Months",
                },
            ],
            selectedDates: null,
            attributes: [
                {
                    highlight: {
                        start: { fillMode: "outline", color: "red" },
                        base: { fillMode: "light", color: "red" },
                        end: { fillMode: "outline", color: "red" },
                    },
                },
            ],
        };
    },
    watch: {
        initDates: {
            handler(newValue) {
                this.selectedDates = newValue;
            },
            immediate: true,
        },
        initRadioState: {
            handler(newValue) {
                this.radioState = newValue;
            },
            immediate: true,
        },
        selectedDates: {
            handler(newValue) {
                if (!this.selectedDates || !this.selectedDates.start || !this.selectedDates.end) return "";

                if (!this.formatEmit) {
                    this.$emit("select", this.selectedDates);
                    return;
                }
                const formatDate = (date) => {
                    const day = String(date.getDate()).padStart(2, "0");
                    const month = String(date.getMonth() + 1).padStart(2, "0");
                    const year = date.getFullYear();
                    return `${year}-${month}-${day}`;
                };
                const returnData = {
                    start: formatDate(this.selectedDates.start),
                    end: formatDate(this.selectedDates.end),
                };
                this.$emit("select", returnData);
            },
            deep: true,
        },
        radioState: {
            handler() {
                this.handleRadioChange();
            },
        },
    },
    methods: {
        waitForDateInput() {
            this.dateDialog = true;
            return new Promise((resolve) => {
                this.resolveDateDialog = (confirmed) => {
                    this.dateDialog = false;
                    resolve(confirmed ? this.customDateFrom : null);
                };
            });
        },
        async onRepeatedClick() {
            if (this.radioState === "customDateRange") {
                await this.handleRadioChange();
            }
        },
        async handleRadioChange() {
            this.selectedDates = null;
            let start, end;
            const now = new Date();

            switch (this.radioState) {
                case "customDateRange":
                    this.customDateFrom = null;
                    start = await this.waitForDateInput();
                    if (!start) {
                        return;
                    }
                    break;
                case "last6Months":
                    start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
                    break;
                case "last3Months":
                    start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
                    break;
                case "currentMonth":
                    start = new Date(now.getFullYear(), now.getMonth(), 1);
                    break;
                case "last12Months":
                    start = new Date(now.getFullYear() - 1, now.getMonth(), 1);
                    break;
                case "last24Months":
                    start = new Date(now.getFullYear() - 2, now.getMonth(), 1);
                    break;
            }

            if (this.selectedDates) return;

            this.$refs.datePickerRef?.$children[0]?.focusDate(now);

            end = now;
            this.selectedDates = { start, end };
        },
    },
};
</script>

<style scoped>
.white-background {
    background-color: white;
}

.large-icon {
    font-size: 2rem;
}

.custom-calendar {
    border: none !important;
}

.nav-button {
    border: 0.5px solid #d1d5db;
    border-radius: 8px;
    height: 3rem;
    width: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

:deep(.vc-header) {
    padding-bottom: 10px;
    padding-top: 17px;
}

:deep(.vc-weeks) {
    display: grid;
    grid-template-columns: repeat(7, minmax(45px, 1fr)) !important;
    grid-auto-rows: minmax(45px, auto) !important;
    align-items: center !important;
}

:deep(.vc-h-full) {
    display: flex;
    align-items: center;
    justify-content: center;
}

:deep(.vc-grid-cell) {
    height: 100%;
}

:deep(.vc-grid-cell-row--7):not(.vc-nav-container .vc-grid-cell-row--7),
:deep(.vc-grid-cell-row-2):not(.vc-nav-container .vc-grid-cell-row-2),
:deep(.vc-grid-cell-row-3):not(.vc-nav-container .vc-grid-cell-row-3),
:deep(.vc-grid-cell-row-4):not(.vc-nav-container .vc-grid-cell-row-4),
:deep(.vc-grid-cell-row-5):not(.vc-nav-container .vc-grid-cell-row-5),
:deep(.vc-grid-cell-row-6):not(.vc-nav-container .vc-grid-cell-row-6) {
    border-bottom: 1px solid rgba(223, 226, 224, 1);
}

:deep(.custom-radio .v-selection-control__input) {
    width: 20px;
    height: 20px;
    border: 2px solid red;
    border-radius: 50%;
    position: relative;
}

:deep(.custom-radio .v-selection-control__input input:checked + .v-selection-control__control) {
    background-color: red;
}

:deep(.custom-radio .v-selection-control__input input:checked + .v-selection-control__control::after) {
    content: "";
    width: 8px;
    height: 8px;
    background-color: white;
    border-radius: 50%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.fa-dot-circle {
    display: none !important;
}
</style>
